'use client'

import type { ProposalOrder } from '@moment/horizon-data-access/proposal'

import { useMemo } from 'react'

import {
  useSecurityItemsByIsin,
  withSecurityInstrument,
} from '@/portfolio/proposal-builder/hooks/use-order-security-instruments'

interface OrdersWithSecurityInstruments {
  /** Empty while the Security read is pending or failed, so no snapshot value shows in its place. */
  orders: ProposalOrder[]
  isLoading: boolean
  error: Error | null
}

/**
 * The orders with their instrument reference data read from SORM Security behind
 * the gate; the orders as given when it is off.
 */
export function useOrdersWithSecurityInstruments(
  orders: ProposalOrder[],
): OrdersWithSecurityInstruments {
  const isins = useMemo(
    () => orders.map((order) => order.instrument_id),
    [orders],
  )
  const security = useSecurityItemsByIsin(isins, 'orders')

  return useMemo((): OrdersWithSecurityInstruments => {
    switch (security.status) {
      case 'disabled':
        return { orders, isLoading: false, error: null }
      case 'failed':
        return { orders: [], isLoading: false, error: security.error }
      case 'loading':
        return orders.length === 0
          ? { orders, isLoading: false, error: null }
          : { orders: [], isLoading: true, error: null }
      case 'ready':
        return {
          orders: orders.map((order) =>
            withSecurityInstrument(order, security.items),
          ),
          isLoading: false,
          error: null,
        }
      default:
        return security satisfies never
    }
  }, [security, orders])
}
